import { Component, OnInit, OnDestroy, ChangeDetectorRef, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Subscription, filter } from "rxjs";
import { DataService } from "../../services/data.service";
import { FormControl } from "@angular/forms";
import { environment } from "src/environments/environment";
import { AuthService } from "../../services/auth/auth.service";
import { DepartmentOption, FacultyOption, PersonalizationPopupData, PopupSelectableOption, MatSelectChange } from "./personalization-popup.types";
@Component({
  selector: 'personalization-popup',
  templateUrl: './personalization-popup.html',
  styleUrls: ['./personalization-popup.scss']
})
export class PersonalizationPopup implements OnInit, OnDestroy {
  campusesForm = new FormControl<string[]>([], { nonNullable: true });
  facultiesForm = new FormControl<string[]>([], { nonNullable: true });
  departmentsForm = new FormControl<string[]>([], { nonNullable: true });
  degreeLevelsForm = new FormControl<string[]>([], { nonNullable: true });
  academicYearsForm = new FormControl<string[]>([], { nonNullable: true });
  departmentOptions: DepartmentOption[] = [];
  popupData?: PersonalizationPopupData;
  isLoading: boolean = true;
  previousSelections: Record<string, string[]> = {};
  departmentsDropdownMobileClasses = ["department-expansion mobile"];
  private subscriptions = new Subscription();
  private dismissHandled = false;
  // True only for the auto-opened popup (first time after switching to the student portal).
  // Manual opens (account menu / sidebar) leave this false.
  private isAutoOpened = false;

  constructor(
    private dataService: DataService,
    private cd: ChangeDetectorRef,
    private dialogRef: MatDialogRef<PersonalizationPopup>,
    @Inject(MAT_DIALOG_DATA) private data: { autoOpened?: boolean } | null
  ) {
    this.isAutoOpened = !!this.data?.autoOpened;
  }

  ngOnInit(): void {
    this.getAllData();

    // Only the auto-opened popup is opened with disableClose:true and must persist
    // "everything selected" + reload on ANY dismiss. Manual opens close normally, so
    // intercepting Esc/backdrop is unnecessary there.
    if (this.isAutoOpened) {
      // Esc key — disableClose:true means Material no longer auto-closes the dialog
      this.subscriptions.add(
        this.dialogRef.keydownEvents()
          .pipe(filter(e => e.key === 'Escape'))
          .subscribe(() => this.dismissAndSaveAll())
      );

      // Backdrop click
      this.subscriptions.add(
        this.dialogRef.backdropClick().subscribe(() => this.dismissAndSaveAll())
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getAllData() {
    const url = environment.apiUrl + '/Portal/Api/PersonalizationPopup/GetInitData';
    this.isLoading = true;
    this.dataService.getDataByToken(url, {
        cultureCode: this.dataService.getCultureCode(),
      }).subscribe({
      next: (responseData: PersonalizationPopupData) => {
        this.popupData = responseData;

        const selectedCampuses = this.popupData.campusesOptions
          .filter((x: PopupSelectableOption) => x.isSelected === true)
          .map((x: PopupSelectableOption) => x.option.key);

        const selectedFaculties = this.popupData.facultiesOptions
          .filter((x: FacultyOption) => x.isSelected === true)
          .map((x: FacultyOption) => x.option.key);

        const selectedDepartmentsOptions: string[] = [];
        const allDepartmentsOptions: PopupSelectableOption[] = [];

        this.popupData.facultiesOptions
          .filter(
            (x: FacultyOption) =>
              x.isSelected === true && x.departmentsOptions?.length
          )
          .forEach((x: FacultyOption) => {
            x.departmentsOptions.forEach((d: PopupSelectableOption) => {
              if (
                !allDepartmentsOptions.some(
                  (departmentOption: PopupSelectableOption) =>
                    departmentOption.option.key === d.option.key
                )
              ) {
                if (d.isSelected && d.option.key !== '-1') {
                  selectedDepartmentsOptions.push(d.option.key);
                }
                allDepartmentsOptions.push(d);
              }
            });
          });

        const allOption = allDepartmentsOptions.find(x => x.option.key === "-1");
        if(allOption){
          allOption.isSelected = allDepartmentsOptions.every(x => x.option.key === "-1" || x.isSelected);
          if(allOption.isSelected) selectedDepartmentsOptions.unshift("-1")
        }
        
        this.popupData.departmentsOptions = allDepartmentsOptions;
        this.departmentOptions = allDepartmentsOptions.map(
          (x: PopupSelectableOption) => x.option as DepartmentOption
        );

        const selectedDepartments: string[] = selectedDepartmentsOptions;

        const selectedDegreeLevels = this.popupData.degreeLevelsOptions
          .filter((x: PopupSelectableOption) => x.isSelected === true)
          .map((x: PopupSelectableOption) => x.option.key);

        const selectedAcademicYears = this.popupData.academicYearsOptions
          .filter((x: PopupSelectableOption) => x.isSelected === true)
          .map((x: PopupSelectableOption) => x.option.key);

        this.previousSelections = {
          campusesForm: [...selectedCampuses],
          facultiesForm: [...selectedFaculties],
          departmentsForm: [...selectedDepartments],
          degreeLevelsForm: [...selectedDegreeLevels],
          academicYearsForm: [...selectedAcademicYears],
        };

        this.campusesForm.setValue(selectedCampuses || [], { emitEvent: false });
        this.facultiesForm.setValue(selectedFaculties || [], { emitEvent: false });
        this.departmentsForm.setValue(selectedDepartments || [], { emitEvent: false });
        this.degreeLevelsForm.setValue(selectedDegreeLevels || [], { emitEvent: false });
        this.academicYearsForm.setValue(selectedAcademicYears || [], { emitEvent: false });
        this.isLoading = false;
        this.cd.detectChanges();
      },
      error: (e: any) => {
        console.error(e);
      },
    });
  }

  onOptionChange(event: MatSelectChange, key: string) {
    const selected = (event.value ?? []) as string[];
    const prev = this.previousSelections[key] ?? [];

    const allOptions =this.getOptionsByForm(key)
      .map((x) => x.option.key);

    const allWithoutAll = allOptions.filter((x: string) => x !== '-1');
    const selectedWithoutAll = selected.filter(x => x !== '-1');

    let result = [...selected];

    // CASE 1: user manually selected all → auto-add "-1"
    const manuallySelectedAll =
      selectedWithoutAll.length === allWithoutAll.length &&
      !selected.includes('-1') &&
      selectedWithoutAll.length > prev.filter(x => x !== '-1').length;

    if (manuallySelectedAll) {
      result = [...allOptions];
    }

    // CASE 2: user deselected "-1" (everything was selected)
    else if (prev.includes('-1') && !selected.includes('-1')) {
      result = [];
    }
    else if (!prev.includes('-1') && selected.includes('-1')) {
      result = [...allOptions];
    }

    // CASE 4: normal case → remove "-1"
    else if (selectedWithoutAll.length < allWithoutAll.length) {
      result = result.filter(x => x !== '-1');
    }

    // update UI
    const form = this.getFormControlByKey(key);
    form.setValue(result, { emitEvent: false });

    this.getOptionsByForm(key).forEach((x: PopupSelectableOption) => {
      x.isSelected = result.includes(x.option.key);
    });

    // store previous
    this.previousSelections[key] = result;
  }

  onFacultyOptionChange(
    event: MatSelectChange | null,
    key: string,
    ignoreSetOption: boolean = false
  ): void {
    if (!ignoreSetOption && event) {
      this.onOptionChange(event, key);
    }

    const allDepartmentsOptions: PopupSelectableOption[] = [];

    this.popupData!.facultiesOptions
      .filter(
        (x: FacultyOption) =>
          x.isSelected === true && x.option.key !== '-1'
      )
      .forEach((x: FacultyOption) => {
        x.departmentsOptions.forEach((d: PopupSelectableOption) => {
          d.isSelected = this.departmentsForm.value.includes(d.option.key);

          if (
            !allDepartmentsOptions.some(
              (departmentOption: PopupSelectableOption) =>
                departmentOption.option.key === d.option.key
            )
          ) {
            allDepartmentsOptions.push(d);
          }
        });
      });

    allDepartmentsOptions.sort((a: PopupSelectableOption, b: PopupSelectableOption) => {
      if (a.option.key === '-1') return -1;
      if (b.option.key === '-1') return 1;
      return a.option.value.localeCompare(b.option.value);
    });

    const allOption = allDepartmentsOptions.find(
      (x: PopupSelectableOption) => x.option.key === '-1'
    );

    if (allOption) {
      allOption.isSelected = allDepartmentsOptions.every(
        (x: PopupSelectableOption) =>
          x.option.key === '-1' || x.isSelected
      );
    }

    this.departmentsForm.setValue(
      allDepartmentsOptions
        .filter((x: PopupSelectableOption) => x.isSelected)
        .map((x: PopupSelectableOption) => x.option.key),
      { emitEvent: false }
    );

    this.departmentOptions = allDepartmentsOptions.map(
      (x: PopupSelectableOption) => x.option as DepartmentOption
    );
  }

  getFormControlByKey(key: string): FormControl<string[]> {
    switch (key) {
      case 'campusesForm': return this.campusesForm;
      case 'facultiesForm': return this.facultiesForm;
      case 'departmentsForm': return this.departmentsForm;
      case 'degreeLevelsForm': return this.degreeLevelsForm;
      case 'academicYearsForm': return this.academicYearsForm;
    }
    throw new Error('Invalid form key: ' + key);
  }

  onCheckboxChange(keyValue: string, checked: boolean, formKey: string) {
    const form = this.getFormControlByKey(formKey);
    let selected = [...(form.value ?? [])];

    if (checked) {
      if (!selected.includes(keyValue)) selected.push(keyValue);
    } else {
      selected = selected.filter(x => x !== keyValue);
    }
    
    const matSelectChangeEvent: MatSelectChange = { value: selected };

    if (formKey === 'facultiesForm') {
      this.onFacultyOptionChange(matSelectChangeEvent, formKey);
    } else {
      this.onOptionChange(matSelectChangeEvent, formKey);
    }
  }

  clearAllOptions() {
    const resetGroup = (options: PopupSelectableOption[],
      formControl: FormControl<string[]>,
      formKey: string) => {
      const defaults: string[] = [];
      options.forEach(o => {
        o.isSelected = o.defaultValue;
        if (o.defaultValue) {
          defaults.push(o.option.key);
        }
      });

      formControl.setValue(defaults, { emitEvent: true });
      this.previousSelections[formKey] = defaults;
    };

    resetGroup(this.popupData!.campusesOptions, this.campusesForm, "campusesForm");
    resetGroup(this.popupData!.facultiesOptions, this.facultiesForm, "degreeLevelsForm");
    this.onFacultyOptionChange(null, "", true);
    this.departmentOptions.forEach((departmentOption) => {
      departmentOption.isSelected = departmentOption.key !== "-1" && 
        this.popupData!.facultiesOptions
          .filter((f) => f.isSelected)
          .some((f) => f.departmentsOptions.some(fd => fd.defaultValue && fd.option.key === departmentOption.key)); 
    });
    this.departmentsForm.setValue(this.departmentOptions.filter(d => d.isSelected).map(d => d.key), { emitEvent: true });
    this.departmentOptions = [...this.departmentOptions];
    this.previousSelections["departmentsForm"] = this.departmentsForm.value
    resetGroup(this.popupData!.degreeLevelsOptions, this.degreeLevelsForm, "degreeLevelsForm");
    resetGroup(this.popupData!.academicYearsOptions, this.academicYearsForm, "academicYearsForm");
  }

  savePersonalization() {
    // Prevent a subsequent Esc/backdrop dismiss from firing a second (all-selected) save
    this.dismissHandled = true;

    const request = {
      campusesIds : this.campusesForm.value,
      facultiesIds: this.facultiesForm.value,
      departmentsIds: this.departmentsForm.value,
      degreeLevelIds: this.degreeLevelsForm.value,
      academicYears: this.academicYearsForm.value,
    }

    const url = environment.apiUrl + '/Portal/Api/PersonalizationPopup/UpdatePersonalization';
    this.dataService.getDataByToken(url, request).subscribe({
      next: () => {
        localStorage.setItem(AuthService.isPersonalizationPopupOpenedKey, "true");
        window.location.reload();
      },
      error: (e: any) => {
        console.error(e);
      },
    });
  }

  // Dismissing the popup any way other than Save (Cancel, X, Esc, backdrop) persists
  // personalization as "everything selected" and suppresses re-opening this login session.
  // The request is built directly from popupData so the visible form/checkboxes are never
  // mutated before the popup closes (no flicker).
  dismissAndSaveAll() {
    if (this.dismissHandled) return;

    // Manual opens (account menu / sidebar): closing just discards — no forced
    // "everything selected" save and no page reload. Save still works normally.
    if (!this.isAutoOpened) {
      this.dialogRef.close();
      return;
    }

    this.dismissHandled = true;

    // Data not loaded yet (Esc during loading) — nothing to build; just suppress & close.
    if (!this.popupData) {
      localStorage.setItem(AuthService.isPersonalizationPopupOpenedKey, "true");
      this.dialogRef.close();
      return;
    }

    const realKeys = (options: PopupSelectableOption[]): string[] =>
      options.map(o => o.option.key).filter(key => key !== '-1');

    // All departments across ALL faculties (popupData.departmentsOptions only holds
    // departments of currently-selected faculties), deduped, excluding the '-1' sentinel.
    const allDepartmentKeys: string[] = [];
    this.popupData.facultiesOptions.forEach((faculty: FacultyOption) => {
      (faculty.departmentsOptions ?? []).forEach((d: PopupSelectableOption) => {
        const key = d.option.key;
        if (key !== '-1' && !allDepartmentKeys.includes(key)) {
          allDepartmentKeys.push(key);
        }
      });
    });

    const request = {
      campusesIds: realKeys(this.popupData.campusesOptions),
      facultiesIds: realKeys(this.popupData.facultiesOptions),
      departmentsIds: allDepartmentKeys,
      degreeLevelIds: realKeys(this.popupData.degreeLevelsOptions),
      academicYears: realKeys(this.popupData.academicYearsOptions),
    };

    // SelectAllPersonalization persists everything as selected (content shows everything)
    // but leaves IsCustomPersonalizationSet=false, so the popup reopens in its default
    // first-time state rather than showing everything ticked.
    const url = environment.apiUrl + '/Portal/Api/PersonalizationPopup/SelectAllPersonalization';
    this.dataService.getDataByToken(url, request).subscribe({
      next: () => {
        localStorage.setItem(AuthService.isPersonalizationPopupOpenedKey, "true");
        window.location.reload();
      },
      error: (e: any) => {
        console.error(e);
        // Still suppress & close so the disableClose dialog isn't a trap and doesn't
        // reopen mid-session on a transient server error.
        localStorage.setItem(AuthService.isPersonalizationPopupOpenedKey, "true");
        this.dialogRef.close();
      },
    });
  }

  getOptionsByForm(key: string): PopupSelectableOption[] {
    switch (key) {
      case 'campusesForm':
        return this.popupData!.campusesOptions;
      case 'facultiesForm':
        return this.popupData!.facultiesOptions;
      case 'departmentsForm':
        return this.popupData!.departmentsOptions;
      case 'degreeLevelsForm':
        return this.popupData!.degreeLevelsOptions;
      case 'academicYearsForm':
        return this.popupData!.academicYearsOptions;
      default:
        return [];
    }
  }

  getPlaceholderByFormKey(formKey: string): string {
    switch (formKey) {
      case 'facultiesForm':
        return this.popupData!.facultiesFormListPlaceholder;
      case 'campusesForm':
        return this.popupData!.campusesFormListPlaceholder;
      case 'degreeLevelsForm':
        return this.popupData!.degreeLevelsFormListPlaceholder;
      case 'academicYearsForm':
        return this.popupData!.academicYearsFormListPlaceholder;
      case 'departmentsForm':
        return this.popupData!.departmentsFormListPlaceholder;
      default:
        return '';
    }
  }

  getOptionsTextByKey(key: string): string[] {
    return this.getOptionsByForm(key)
      .filter((x: PopupSelectableOption) => x.isSelected)
      .map((x: PopupSelectableOption) => x.option.value);
  }

  getSelectedOptionsLength(key: string): number {
    return this.getOptionsByForm(key)
      .filter((x: PopupSelectableOption) => x.isSelected).length;
  }

  isAllSelected(key: string): boolean {
    const options = this.getOptionsByForm(key);
    return options.length > 0 && options.every(x => x.isSelected);
  }
}
